import { ReadonlyVec3 } from 'gl-matrix';
import {
    enclosedAreaMm2OnSliceAt,
    isInsideAt,
    StructureSdf,
} from './structureSdf';

/**
 * Minimal shape of an ROI this lookup needs.
 *
 * `sdf` is nullable because a structure need not have a field rasterised
 * yet; resolving one is the caller's job, since the engine decides whether
 * that costs a rasterisation.
 */
export interface DensityOverrideRoi {
    readonly name: string;
    readonly sdf: StructureSdf | null;
    readonly electronDensityOverride: number | undefined;
    readonly massDensityOverride: number | undefined;
}

/**
 * Whether a structure carries a density override at all. The cheap test to
 * apply before anything that costs a field read: a structure without one
 * can never be the answer, so its field is never worth fetching.
 */
export function carriesDensityOverride(roi: {
    readonly electronDensityOverride: number | undefined;
    readonly massDensityOverride: number | undefined;
}): boolean {
    return (
        roi.electronDensityOverride !== undefined ||
        roi.massDensityOverride !== undefined
    );
}

/**
 * The density override in force at a point: the responsible ROI's name
 * and whichever override quantities it carries. Densities are relative
 * (unitless) — they get no unit suffix in the name or the display.
 */
export interface DensityOverrideAtPoint {
    roiName: string;
    relElectron?: number;
    relMass?: number;
}

/**
 * The density override a TPS would apply at `patientPoint`, or undefined
 * when no override-carrying ROI contains it.
 *
 * Containment is read from each structure's signed-distance field, which
 * is what a structure IS here. The DICOM contour sequence is not: it
 * exists only for structures that arrived as DICOM or have been written
 * back out, so a couch the user has just applied would report no override
 * at all until the set had been saved and reloaded.
 *
 * Each field is sampled in its OWN grid, so nothing here depends on the
 * structures sharing the image's lattice, and a point outside a field's
 * extent is simply outside that structure.
 *
 * When several overriding ROIs contain the point, the innermost wins:
 * smallest enclosed area on the point's slice, measured in mm² because
 * two structures' fields need not share a lattice. Overlapping overrides
 * are therefore reported innermost-first, which assumes the receiving TPS
 * resolves them the same way; against one that does not, this readout would
 * name a different value than the one dose is computed through. A couch
 * this application writes raises no such question — its components are cut
 * out of one another and do not overlap — but an imported one, or two
 * structures the user overrode by hand, can. Exact ties keep the first ROI in structure-set order, so the
 * result is deterministic; which of two identical-size ROIs that is
 * carries no meaning.
 *
 * Cost is bounded per cursor move by ordering the work cheap-first: the
 * override filter, then one field sample per surviving ROI, and only then
 * a slice count — for the ROIs that actually contain the point, which is
 * one or two, and whose fields are cropped to a couch rather than to an
 * image.
 */
export function densityOverrideAtPoint(
    rois: readonly DensityOverrideRoi[],
    patientPoint: ReadonlyVec3
): DensityOverrideAtPoint | undefined {
    let bestRoi: DensityOverrideRoi | undefined;
    let bestArea = Infinity;
    for (const roi of rois) {
        if (roi.sdf === null || !carriesDensityOverride(roi)) {
            continue;
        }
        if (!isInsideAt(roi.sdf, patientPoint)) {
            continue;
        }
        const area = enclosedAreaMm2OnSliceAt(roi.sdf, patientPoint);
        if (area < bestArea) {
            bestArea = area;
            bestRoi = roi;
        }
    }

    if (!bestRoi) {
        return undefined;
    }
    return {
        roiName: bestRoi.name,
        ...(bestRoi.electronDensityOverride !== undefined && {
            relElectron: bestRoi.electronDensityOverride,
        }),
        ...(bestRoi.massDensityOverride !== undefined && {
            relMass: bestRoi.massDensityOverride,
        }),
    };
}
